import { useEffect, useMemo, useState } from 'react';
import { DisplayPreferencesContext } from './useDisplayPreferences';
import type { PropsWithChildren } from 'react';

const DISPLAY_MONO_LITERALS_STORAGE_KEY = 'flowqa-display-prefers-mono-literals';

const readPrefersMonospaceLiterals = (): boolean => {
  if (typeof window === 'undefined') return false;

  try {
    return window.localStorage.getItem(DISPLAY_MONO_LITERALS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

export const DisplayPreferencesProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const [prefersMonospaceLiterals, setPrefersMonospaceLiterals] = useState<boolean>(() =>
    readPrefersMonospaceLiterals(),
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      if (prefersMonospaceLiterals) {
        window.localStorage.setItem(DISPLAY_MONO_LITERALS_STORAGE_KEY, '1');
        return;
      }

      window.localStorage.removeItem(DISPLAY_MONO_LITERALS_STORAGE_KEY);
    } catch {
      // Ignore storage failures so the preference still works in-memory.
    }
  }, [prefersMonospaceLiterals]);

  const value = useMemo(
    () => ({ prefersMonospaceLiterals, setPrefersMonospaceLiterals }),
    [prefersMonospaceLiterals],
  );

  return (
    <DisplayPreferencesContext.Provider value={value}>
      {children}
    </DisplayPreferencesContext.Provider>
  );
};
